import _ from "lodash";
import React from "react";
import {
  STUDENT_SIGN_IN_CODE,
  FAMILY_INVITE_CODE,
  REMOVE,
  RETAIN,
  LOGOUT_STUDENTS_IN_BULK,
  LOGOUT_STUDENT,
} from "Constants/stringConstants";
import {
  LogoutOutlined,
  DownloadOutlined,
  ReloadArrowOutlined,
  DisableOutlined,
} from "@toddle-edu/ds-icons";
import { canResetStudentCodeAndFamilyInviteInSchoolRoster } from "SchoolSetup/modules/Utils";
import ACLStore from "lib/aclStore";
import {
  canResetStudentSignInAndFamilyInviteCodesInClassSettings,
  canViewStudentSignInAndFamilyInviteCodesInClassSettings,
} from "Settings/modules/SettingsUtils";
import { disableEditForMapleBearChildOrg } from "Courses/modules/utils";

const getStudentFeedQueryVariables = ({
  searchText,
  courses,
  archivalState,
  blockStatus,
  curriculumProgramIds,
  searchTextFields,
  yearGroups,
  academicYearId,
  orderBy,
  orderByDirection,
  includedStudentIds,
  academicYearIds,
}) => {
  return {
    courses,
    searchText,
    curriculumProgramIds,
    archivalState,
    blockStatus,
    searchTextFields,
    yearGroups,
    ...(academicYearIds ? { academicYearIds } : { academicYearId }),
    orderBy,
    orderByDirection,
    includedStudentIds,
  };
};

const getResponseModalQueryFilters = ({
  organizationId,
  filters,
  studentCurriculumProgramFilter,
  familyMembersCountFilter,
  userCourseMapFilters,
  academicYearId,
  resetEntity,
}) => {
  let props = {
    organizationId,
    filters,
    studentCurriculumProgramFilter,
  };

  switch (resetEntity) {
    case STUDENT_SIGN_IN_CODE:
      props = {
        ...props,
        userCourseMapFilters,
        userYearGroupMapFilters: {
          academicYearIds: academicYearId ? [academicYearId] : [],
        },
        courseGradeFilters: { academicYearId },
        yearGroupGradeFilters: { academicYearId },
      };
      break;
    case FAMILY_INVITE_CODE:
      props = {
        ...props,
        familyMembersCountFilter,
      };
      break;
  }

  return props;
};

const getStudentFeedQueryVariablesMemoized = _.memoize(
  params => getStudentFeedQueryVariables(params),
  params => JSON.stringify(params)
);

const getResponseModalQueryFiltersMemoized = _.memoize(
  params => getResponseModalQueryFilters(params),
  params => JSON.stringify(params)
);

const getResetCodeMenuItemsForClassSettings = ({
  t,
  isStudentTab,
  isPreviousAcademicYearSelected,
  isFamilyInviteOptionsDisabled,
}) => {
  let RESET_CODE_MENU_ITEMS = [];
  if (isStudentTab) {
    RESET_CODE_MENU_ITEMS = [
      {
        key: "reset_student_sign_in_code",
        label: t("common:reset_all_with_label", {
          label: _.lowerCase(t("common:student_sign_in_code_plural")),
        }),
        isDestructive: true,
        icon: <ReloadArrowOutlined />,
        visible:
          !isPreviousAcademicYearSelected &&
          !disableEditForMapleBearChildOrg() &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings(),
      },
      {
        label: t("common:reset_all_with_label", {
          label: t("common:family_invite_code_plural"),
        }),
        key: "reset_family_code",
        isDestructive: true,
        icon: <ReloadArrowOutlined />,
        visible:
          !isPreviousAcademicYearSelected &&
          !disableEditForMapleBearChildOrg() &&
          canResetStudentSignInAndFamilyInviteCodesInClassSettings() &&
          !isFamilyInviteOptionsDisabled,
      },
    ];
  } else {
    RESET_CODE_MENU_ITEMS = [
      {
        label: t("common:download_with_label", {
          label: t("common:student_list_of_unconnected_family_xlsx"),
        }),
        key: "student_list_of_unconnected_family",
        icon: <DownloadOutlined />,
        visible: canViewStudentSignInAndFamilyInviteCodesInClassSettings(),
      },
      {
        label: t("common:reset_with_label", {
          label: t("common:family_invite_code_plural"),
        }),
        key: "reset_family_code",
        isDestructive: true,
        icon: <ReloadArrowOutlined />,
        visible:
          canResetStudentSignInAndFamilyInviteCodesInClassSettings() &&
          !disableEditForMapleBearChildOrg() &&
          !isPreviousAcademicYearSelected &&
          !isFamilyInviteOptionsDisabled,
      },
    ];
  }

  return RESET_CODE_MENU_ITEMS.filter(item => item.visible);
};

const getResetCodeMenuItemsForSchoolSetup = ({
  t,
  isStudentTab,
  isCurrentAcademicYearSelected,
  unConnectedStudentPresent,
  curriculumProgramId,
  isFutureAcademicYearSelected,
  isFamilyInviteOptionsDisabled,
}) => {
  let RESET_CODE_MENU_ITEMS = [];

  if (isStudentTab) {
    RESET_CODE_MENU_ITEMS = [
      {
        key: "reset_student_sign_in_code",
        label: t("common:reset_with_label", {
          label: _.lowerCase(t("common:student_sign_in_code_plural")),
        }),
        visible:
          canResetStudentCodeAndFamilyInviteInSchoolRoster(
            curriculumProgramId
          ) &&
          !disableEditForMapleBearChildOrg() &&
          (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
        icon: <ReloadArrowOutlined />,
        isDestructive: true,
      },
      {
        label: t("schoolSetup:logout_user_from_all_devices", {
          label: _.toLower(t("common:student_plural")),
        }),
        key: "logout_students",
        visible:
          canResetStudentCodeAndFamilyInviteInSchoolRoster(
            curriculumProgramId
          ) &&
          (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
        icon: <LogoutOutlined />,
        isDestructive: true,
      },
    ];
  } else {
    RESET_CODE_MENU_ITEMS = [
      {
        key: "family",
        label: t("common:family"),
        isItemGroup: true,
        options: [
          {
            label: t("common:download_with_label_in_xlsx", {
              label: t("common:student_list_of_unconnected_family"),
            }),
            key: "student_list_of_unconnected_family",
            disabled: unConnectedStudentPresent,
            visible: !ACLStore.can("Common:DisableStudentInviteCodeUsage"),
            icon: <DownloadOutlined />,
          },
          {
            label: t("common:download_with_label_in_xlsx", {
              label: t("common:all_family_members_with_label", {
                label: t("common:all"),
              }),
            }),
            key: "all_family_members",
            visible: true,
            icon: <DownloadOutlined />,
          },
          {
            label: t("common:reset_with_label", {
              label: t("common:family_invite_code_plural"),
            }),
            key: "reset_family_code",
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                curriculumProgramId
              ) &&
              !disableEditForMapleBearChildOrg() &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected) &&
              !isFamilyInviteOptionsDisabled,
            isDestructive: true,
            icon: <ReloadArrowOutlined />,
          },
        ],
      },
      {
        key: "students",
        label: t("common:student_plural"),
        isItemGroup: true,
        options: [
          {
            label: t("common:download_with_label_in_xlsx", {
              label: t("common:student_list"),
            }),
            visible: true,
            key: "student",
            icon: <DownloadOutlined />,
          },
          {
            key: "bulk_unblock_students",
            label: t("schoolSetup:unblock_student_access"),
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                curriculumProgramId
              ) &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
            icon: <DisableOutlined />,
          },
          {
            key: "bulk_block_students",
            label: t("schoolSetup:temporarily_block_student_access"),
            isDestructive: true,
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                curriculumProgramId
              ) &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
            icon: <DisableOutlined />,
          },
          {
            key: "reset_student_sign_in_code",
            label: t("common:reset_with_label", {
              label: _.lowerCase(t("common:student_sign_in_code_plural")),
            }),
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                curriculumProgramId
              ) &&
              !disableEditForMapleBearChildOrg() &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
            isDestructive: true,
            icon: <ReloadArrowOutlined />,
          },
          {
            label: t("schoolSetup:logout_user_from_all_devices", {
              label: _.toLower(t("common:student_plural")),
            }),
            key: "logout_students",
            visible:
              canResetStudentCodeAndFamilyInviteInSchoolRoster(
                curriculumProgramId
              ) &&
              (isCurrentAcademicYearSelected || isFutureAcademicYearSelected),
            isDestructive: true,
            icon: <LogoutOutlined />,
          },
        ],
      },
    ];
  }

  return isStudentTab
    ? RESET_CODE_MENU_ITEMS.filter(item => item.visible)
    : RESET_CODE_MENU_ITEMS.map(item => ({
        ...item,
        options: item.options?.filter(option => option.visible) || [],
      }));
};

const getResetCodeMenuItems = ({
  t,
  isStudentTab,
  isCurrentAcademicYearSelected,
  isContextClassSettings = false,
  unConnectedStudentPresent,
  curriculumProgramId,
  isFutureAcademicYearSelected,
  isFamilyInviteOptionsDisabled = false,
}) => {
  const isPreviousAcademicYearSelected = !(
    isCurrentAcademicYearSelected || isFutureAcademicYearSelected
  );
  if (isContextClassSettings) {
    return getResetCodeMenuItemsForClassSettings({
      t,
      isStudentTab,
      isPreviousAcademicYearSelected,
      isFamilyInviteOptionsDisabled,
    });
  } else {
    return getResetCodeMenuItemsForSchoolSetup({
      t,
      isStudentTab,
      isCurrentAcademicYearSelected,
      unConnectedStudentPresent,
      curriculumProgramId,
      isFutureAcademicYearSelected,
      isFamilyInviteOptionsDisabled,
    });
  }
};

const handleActionClick = ({
  e,
  updateStateInResetInviteAndSignInCodeModal,
  toggleModal,
  src = "",
}) => {
  const key = e.key;

  switch (key) {
    case "reset_family_code":
      updateStateInResetInviteAndSignInCodeModal({
        mode: "BULK",
        resetEntity: FAMILY_INVITE_CODE,
        src,
      });
      toggleModal({
        type: "FEED",
        isVisible: true,
      });
      break;
    case "reset_student_sign_in_code":
      updateStateInResetInviteAndSignInCodeModal({
        mode: "BULK",
        resetEntity: STUDENT_SIGN_IN_CODE,
        src,
      });
      toggleModal({
        type: "FEED",
        isVisible: true,
      });
      break;
    case "logout_students":
      updateStateInResetInviteAndSignInCodeModal({
        mode: "BULK",
        resetEntity: LOGOUT_STUDENTS_IN_BULK,
        src,
      });
      toggleModal({
        type: "FEED",
        isVisible: true,
      });
      break;
  }
};

const getCardSelectionOptions = ({
  t,
  resetEntity,
  totalCount,
  isSingleResetMode,
}) => {
  const resetStudentSignInCardSelectedOptions = [
    {
      value: RETAIN,
      label:
        _.isEqual(totalCount, 1) || isSingleResetMode
          ? t("schoolSetup:retain_access_for_students_with_bulk_label")
          : t("schoolSetup:retain_access_for_students_with_bulk_label_plural"),
    },
    {
      value: REMOVE,
      label:
        isSingleResetMode || _.isEqual(totalCount, 1)
          ? t("schoolSetup:logout_with_label", {
              label: _.lowerCase(t("common:student")),
            })
          : t("schoolSetup:logout_students_with_count", {
              count: totalCount,
            }),
    },
  ];

  const resetFamilyInviteCardSelectedOptions = [
    {
      value: RETAIN,
      label: t("schoolSetup:retain_access_for_family_members"),
    },
    {
      value: REMOVE,
      label: t("schoolSetup:remove_access_for_family_members"),
    },
  ];

  switch (resetEntity) {
    case STUDENT_SIGN_IN_CODE:
      return resetStudentSignInCardSelectedOptions;
    case FAMILY_INVITE_CODE:
      return resetFamilyInviteCardSelectedOptions;
  }
};

export const getSingleEntityAlertMessage = ({ resetEntity, entityName, t }) => {
  switch (resetEntity) {
    case LOGOUT_STUDENT:
      return t("schoolSetup:logout_student_alert_text_with_name", {
        studentName: entityName,
      });
    case STUDENT_SIGN_IN_CODE:
      return t("schoolSetup:reset_signin_code_alert_text_with_name", {
        studentName: entityName,
      });
    case FAMILY_INVITE_CODE:
      return t("schoolSetup:reset_invite_code_alert_text_with_name", {
        studentName: entityName,
      });
    default:
      return null;
  }
};

export const getBulkEntityAlertMessage = ({ resetEntity, entityCount, t }) => {
  switch (resetEntity) {
    case LOGOUT_STUDENTS_IN_BULK:
      return t("schoolSetup:logout_student_alert_text", {
        count: entityCount,
      });
    case STUDENT_SIGN_IN_CODE:
      return t("schoolSetup:reset_signin_code_alert_text", {
        count: entityCount,
      });
    case FAMILY_INVITE_CODE:
      return t("schoolSetup:reset_invite_code_alert_text", {
        count: entityCount,
      });
    default:
      return null;
  }
};

export {
  getStudentFeedQueryVariablesMemoized,
  getResetCodeMenuItems,
  handleActionClick,
  getResponseModalQueryFiltersMemoized,
  getCardSelectionOptions,
};
